import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  schedulesService,
  CreateScheduleDto,
  UpdateScheduleDto,
  CreateShiftDto,
  UpdateShiftDto,
  CreateSwapRequestDto,
  ReviewSwapRequestDto,
} from '../services/schedules.service';

// Schedule hooks
export function useSchedules(organizationId?: string) {
  return useQuery({
    queryKey: ['schedules', organizationId],
    queryFn: () => schedulesService.getAll(organizationId),
  });
}

export function useSchedule(id: string) {
  return useQuery({
    queryKey: ['schedules', id],
    queryFn: () => schedulesService.getById(id),
    enabled: !!id,
  });
}

export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (schedule: CreateScheduleDto) => schedulesService.create(schedule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateScheduleDto }) =>
      schedulesService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedules', data.id] });
    },
  });
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function usePublishSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.publish(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedules', data.id] });
    },
  });
}

export function useArchiveSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.archive(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedules', data.id] });
    },
  });
}

export function useScheduleStats(id: string) {
  return useQuery({
    queryKey: ['schedules', id, 'stats'],
    queryFn: () => schedulesService.getStats(id),
    enabled: !!id,
  });
}

// Shift hooks
export function useShifts(scheduleId?: string, agentId?: string) {
  return useQuery({
    queryKey: ['shifts', scheduleId, agentId],
    queryFn: () => schedulesService.getShifts(scheduleId, agentId),
  });
}

export function useShift(id: string) {
  return useQuery({
    queryKey: ['shifts', id],
    queryFn: () => schedulesService.getShift(id),
    enabled: !!id,
  });
}

export function useCreateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shift: CreateShiftDto) => schedulesService.createShift(shift),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useUpdateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateShiftDto }) =>
      schedulesService.updateShift(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useDeleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.deleteShift(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

// Bulk operations
export function useCreateBulkShifts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (shifts: CreateShiftDto[]) => schedulesService.createBulkShifts(shifts),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useUpdateBulkShifts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: { id: string; data: UpdateShiftDto }[]) =>
      schedulesService.updateBulkShifts(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useDeleteBulkShifts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => schedulesService.deleteBulkShifts(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

// Schedule versioning and templates
export function useDuplicateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useSaveAsTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, templateName }: { id: string; templateName: string }) =>
      schedulesService.saveAsTemplate(id, templateName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
}

export function useTemplates(organizationId?: string) {
  return useQuery({
    queryKey: ['templates', organizationId],
    queryFn: () => schedulesService.getTemplates(organizationId),
  });
}

export function useCreateFromTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      name,
      startDate,
      endDate,
    }: {
      templateId: string;
      name: string;
      startDate: string;
      endDate: string;
    }) => schedulesService.createFromTemplate(templateId, name, startDate, endDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

// Shift swap request hooks
export function useSwapRequests(organizationId?: string, status?: string) {
  return useQuery({
    queryKey: ['swap-requests', organizationId, status],
    queryFn: () => schedulesService.getSwapRequests(organizationId, status),
  });
}

export function useSwapRequest(id: string) {
  return useQuery({
    queryKey: ['swap-requests', id],
    queryFn: () => schedulesService.getSwapRequest(id),
    enabled: !!id,
  });
}

export function useCreateSwapRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateSwapRequestDto) => schedulesService.createSwapRequest(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swap-requests'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}

export function useReviewSwapRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: ReviewSwapRequestDto }) =>
      schedulesService.reviewSwapRequest(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swap-requests'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}

export function useCancelSwapRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => schedulesService.cancelSwapRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['swap-requests'] });
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
    },
  });
}

// Coverage analytics hook
export function useCoverageAnalytics(scheduleId: string) {
  return useQuery({
    queryKey: ['schedules', scheduleId, 'coverage-analytics'],
    queryFn: () => schedulesService.getCoverageAnalytics(scheduleId),
    enabled: !!scheduleId,
  });
}

// Auto-scheduling hook
export function useAutoSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scheduleId, params }: {
      scheduleId: string;
      params: {
        startDate: string;
        endDate: string;
        shiftsPerDay?: number;
        shiftDurationHours?: number;
        targetCoverage?: number;
      };
    }) => schedulesService.autoSchedule(scheduleId, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shifts'] });
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });
}
